import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  LabeledTextArea,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../../constants/app';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import { createFleetServiceJob, createFleetServiceJobEvidences } from '../../../../services/fleetService';
import { apiParamsState } from '../../../../storage/app';
import { FleetJobType, FleetServiceJob, FleetServiceJobRequest } from '../../../../types/fleet';
import { preventMinus } from '../../../../utils/formUtilis';
import LocalizedDatePicker from '../../../shared/LocalizedDatePicker';
import MultipleDocumentUploader from '../../../shared/MultipleDocumentUploader';
import FleetAssetSelect from '../../shared/FleetAssetSelect';

type Props = Readonly<{
  afterSave: () => void;
  assetId?: string;
  jobType: FleetJobType;
  onCancel: () => void;
  serviceId?: string;
  lastMaintenance?: FleetServiceJob;
}>;

export function FleetServiceJobForm({ jobType, assetId, onCancel, afterSave, serviceId, lastMaintenance }: Props) {
  const { css, theme } = useStyles();

  const apiParams = useRecoilValue(apiParamsState);
  const lastMaintenanceDate = lastMaintenance?.date ? lastMaintenance.date : Date.now();
  const [editingServiceJob, setEditingServiceJob] = useState<FleetServiceJobRequest>({
    clientId: apiParams?.clientId ?? '',
    subClientId: apiParams?.subClientId ?? '',
    assetId,
    cost: 0,
    date: Date.now(),
    type: jobType,
    description: '',
    evidences: [],
    lastMaintenanceDate: jobType === FleetJobType.repair ? lastMaintenanceDate : undefined,
    serviceId,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (apiParams) {
      setEditingServiceJob((prev) => ({
        ...prev,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
      }));
    }
  }, [apiParams]);

  useEffect(() => {
    if (assetId) {
      setEditingServiceJob((prev) => ({
        ...prev,
        assetId,
      }));
    }
  }, [assetId]);

  const canSubmit = useMemo(() => {
    return (
      !!editingServiceJob.assetId &&
      !!editingServiceJob.date &&
      !!editingServiceJob.type &&
      editingServiceJob.cost >= 0 &&
      (jobType === FleetJobType.repair ? !!editingServiceJob.lastMaintenanceDate : true)
    );
  }, [
    editingServiceJob.assetId,
    editingServiceJob.cost,
    editingServiceJob.date,
    editingServiceJob.lastMaintenanceDate,
    editingServiceJob.type,
    jobType,
  ]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetServiceJobRequest) => {
    setEditingServiceJob((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!editingServiceJob.assetId || !apiParams) return;
    startLoading();
    const evidences = [];
    if (editingServiceJob.evidenceFiles && editingServiceJob.evidenceFiles.length > 0) {
      const { paths, error } = await createFleetServiceJobEvidences({
        assetId: editingServiceJob.assetId,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
        jobType: editingServiceJob.type,
        files: editingServiceJob.evidenceFiles.filter((row) => row.status !== 'error').map((row) => row.file),
      });
      if (!error && paths) {
        evidences.push(...paths);
      } else if (error) {
        showFailFeedback(error);
      }
    }
    const { evidenceFiles, ...formRequest } = editingServiceJob;
    const { id, error } = await createFleetServiceJob(editingServiceJob.assetId, {
      ...formRequest,
      evidences,
    });
    const feedbackPrefix =
      jobType === FleetJobType.maintenance ? FEEDBACK_PREFIXES.fleetMaintenance : FEEDBACK_PREFIXES.fleetRepair;
    if (!error && id) {
      afterSave();
      showPositiveFeedback(FEEDBACK.created(feedbackPrefix));
    } else {
      showFailFeedback(FEEDBACK.failedCreation(feedbackPrefix));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        {!assetId && (
          <FleetAssetSelect
            selectedAssetId={editingServiceJob.assetId}
            onChangeAssetId={(assetId) => {
              onInputChange(assetId, 'assetId');
            }}
            required
          />
        )}
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Fecha: *</LabelSmall>
          <LocalizedDatePicker
            value={editingServiceJob.date ? [new Date(editingServiceJob.date)] : null}
            onChange={({ date }) => {
              if (date && !Array.isArray(date)) {
                onInputChange(date.getTime(), 'date');
              }
            }}
            onClose={() => {
              onInputChange(editingServiceJob.date, 'date');
            }}
            size="compact"
            maxDate={new Date()}
            overrides={{
              Input: {
                props: {
                  overrides: {
                    Input: {
                      props: {
                        tabIndex: -1,
                      },
                    },
                  },
                },
              },
            }}
          />
        </FlexColumn>
        <LabeledTextArea
          label="Descripción:"
          value={editingServiceJob.description ?? ''}
          onChangeValue={(value) => {
            onInputChange(value, 'description');
          }}
          placeholder="Describe el trabajo realizado"
        />
        <LabeledInput
          label="Costo:"
          value={editingServiceJob.cost ?? ''}
          placeholder="Ingresa el costo"
          type="number"
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          onChange={(value) => onInputChange((+value).toString(), 'cost')}
          startEnhancer="$"
          min={0}
          required
        />
        {jobType === FleetJobType.repair && (
          <>
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>Fecha último mantenimiento: *</LabelSmall>
              <LocalizedDatePicker
                value={editingServiceJob.lastMaintenanceDate ? [new Date(editingServiceJob.lastMaintenanceDate)] : null}
                onChange={({ date }) => {
                  if (date && !(date instanceof Array)) {
                    onInputChange(date.getTime(), 'lastMaintenanceDate');
                  }
                }}
                onClose={() => {
                  onInputChange(editingServiceJob.lastMaintenanceDate, 'lastMaintenanceDate');
                }}
                size="compact"
                maxDate={new Date()}
              />
            </FlexColumn>
            {!!lastMaintenance && (
              <FlexColumn gap={theme.sizing.scale200}>
                <LabeledInput
                  label="Del último mantenimiento:"
                  value={lastMaintenance.description || 'Sin descripción'}
                  caption="Dato informativo"
                  readonly
                />
              </FlexColumn>
            )}
          </>
        )}
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Evidencias:</LabelSmall>
          <MultipleDocumentUploader
            files={editingServiceJob.evidenceFiles}
            onFilesChange={(files) => {
              onInputChange(files, 'evidenceFiles');
            }}
          />
        </FlexColumn>
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={onCancel}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
